import clsx from 'clsx';
import { useCallback, useMemo } from 'react';

import { WidgetItemAspectRatioDefault, WidgetSingleRowScrollLayoutConfiguration } from '@vestico/model';

import { CustomCSSProperties } from '../../../model';
import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import { LayoutLoadMoreHandler } from './LayoutLoadMoreHandler';
import style from './RowLayoutScroll.module.css';

export interface RowLayoutScrollProps extends BaseLayoutProps {
  layout: WidgetSingleRowScrollLayoutConfiguration;
}

export const RowLayoutScroll = ({ layout, responses, onResponseClick, loadMore, loading, hasMore }: RowLayoutScrollProps) => {
  const responsePreviewTexts = usePreviewTexts(layout, responses);

  const aspectRatio = useMemo(() => layout.aspectRatio ?? WidgetItemAspectRatioDefault, [layout.aspectRatio]);
  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);

  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const customCSS = useMemo(() => {
    const css: CustomCSSProperties = {};
    const { itemPadding, carouselInset } = layout;

    if (itemPadding !== undefined && !Number.isNaN(itemPadding)) {
      css['--vw-rowlayout-item-padding'] = `${itemPadding}px`;
    }

    if (carouselInset !== undefined && !Number.isNaN(carouselInset)) {
      css['--vw-rowlayout-inset'] = `${carouselInset}px`;
    }
    return css;
  }, [layout]);

  const { styles, borderRadiusStyles } = useCarouselStyles({ borderRadius: layout.itemBorderRadius, itemSize: layout.itemSize, customCSS });

  const responseMarkups = useMemo(() => {
    const markups = responses.map((r, idx) => (
      <CarouselItem
        key={r.postDocumentID}
        index={idx}
        response={r}
        onClick={handleResponseClick}
        previewText={responsePreviewTexts[idx]}
        showLikes={layout.showLikes}
        aspectRatio={aspectRatio}
        updateInView={onUpdateInView(idx)}
        playVideo={firstInViewVideoResponse === r}
        borderRadius={borderRadiusStyles}
      />
    ));

    const loadMoreHandler = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} isItem /> : null;
    return [...markups, loadMoreHandler];
  }, [
    hasMore,
    aspectRatio,
    borderRadiusStyles,
    firstInViewVideoResponse,
    handleResponseClick,
    layout.showLikes,
    loadMore,
    loading,
    onUpdateInView,
    responsePreviewTexts,
    responses,
  ]);

  return (
    <div className={clsx(style.root)} style={styles}>
      {responseMarkups}
    </div>
  );
};
